export const STALE_DUPLICATE_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface PrunableFcmToken {
  token: string;
  deviceId?: string;
  lastSeenClient?: unknown;
  lastUsed?: unknown;
}

/**
 * Client check-in time. Missing, empty, or unparseable is unknown — not the
 * epoch. A stored 0 is a real timestamp.
 */
export function clientCheckInMillis(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? undefined : ms;
  }
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? undefined : ms;
  }
  if (typeof value === "object" && typeof (value as { toMillis?: unknown }).toMillis === "function") {
    const ms = (value as { toMillis: () => number }).toMillis();
    return typeof ms === "number" && Number.isFinite(ms) ? ms : undefined;
  }
  return undefined;
}

/**
 * True only for an extra row of the device that is checking in.
 *
 * deviceId is stored in this browser profile, so it ties the row to this
 * device. The row being saved (same token) is updated in place and stays.
 * A missing lastSeenClient is unknown, so the row stays. platform and
 * userAgent are not consulted: they are shared by every device on the same
 * OS and browser version.
 */
export function isAbandonedRegistrationOfThisDevice(
  row: PrunableFcmToken,
  incoming: { token: string; deviceId?: string },
  nowMs: number,
  staleAfterMs: number = STALE_DUPLICATE_WINDOW_MS,
): boolean {
  if (!incoming.deviceId || row.deviceId !== incoming.deviceId) return false;
  if (row.token === incoming.token) return false;
  const seenMs = clientCheckInMillis(row.lastSeenClient);
  if (seenMs === undefined) return false;
  return nowMs - seenMs > staleAfterMs;
}
